import React, { useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "@ui-components/Button";
import Select from "@ui-components/Select";
import SearchSelect from "@ui-components/SearchSelect";
import Badge from "@ui-components/Badge";
import Alert from "@ui-components/Alert";
import {
  CONNETTORI_INPUT_TYPES,
  CONNETTORI_VERTICALI_INPUT_STRUCTURE,
  CONNETTORI_LEGNO_INPUT_STRUCTURE,
} from "@feature/services/connettori";
import _ from "lodash";
import Input from "../ui-components/Input";


const QuestionSelector = ({label, blocks, steps, data, setData, path, disabled}) => {

  // utilities
  const generateBlockOptions = (blocks, grouped=false, withEmptyOption=true) => {
    const stepsCache = {};
    const genericGroupIdentifier = "NO STEP";
    const groups = blocks.reduce(
      (acc, b) => {
        if (grouped && !(b.step_id in stepsCache)) {
          const step = steps.find((s) => s.id === b.step_id);
          if (!!step)
            stepsCache[step.id] = step.name;
        }
        const groupKey = grouped
          ? stepsCache[b.step_id] || genericGroupIdentifier
          : genericGroupIdentifier;
        return {
          ...acc,
          [groupKey]: [
            ...(acc[groupKey] || []),
            {label: b.name, value: b.id},
          ].sort((a, b) => a.label.localeCompare(b.label)),
        };
      },
      {},
    );
    const result = grouped
      ? Object.entries(groups).map(([k, v]) => ({label: `STEP: ${k}`, options: v}))
      : groups[genericGroupIdentifier];
    return [
      ...(withEmptyOption ? [{label: "", value: ""}] : []),
      ...(result || []),
    ];
  }
  const groupedOptions = generateBlockOptions(blocks, true, false);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-gray-500">
        {label}
      </p>
      <SearchSelect
        options={groupedOptions}
        value={
          groupedOptions
            .reduce((acc, g) => [...acc, ...g.options], [])
            .find((o) => o.value === _.get(data, ["blocks_mappings", ...path]))
        }
        onChange={(selected) => {
          setData({
            ...(data || {}),
            blocks_mappings: _.set(
              data.blocks_mappings || {},
              path,
              selected?.value,
            ),
          });
        }}
        isDisabled={disabled}
        isClearable
        mainStyle="border focus:ring-am-500 focus:border-black-500 block w-full text-black-500 sm:text-sm border-gray-300 disabled:opacity-50"
      />
    </div>
  )
};


const ConnectorInputConfigurator = ({structure, object, questions, data, updateConnection, disabled}) => {

  // hooks
  const intl = useIntl();

  // utilities
  const isDummyParent = (parent) => parent === "__";
  const getGroupContainerCls = (parent) => !isDummyParent(parent)
    ? "border-l-4 border-gray-100 pl-4"
    : "";

  return <>
    {Object.entries(structure).map(([parent, columns]) => (
      <React.Fragment key={parent}>
        {!isDummyParent(parent) && (
          <h4 className="flex flex-row items-center gap-1.5">
            <Badge text="GROUP" type="success" textSize="text-xs"/>
            <p className="font-semibold mb-0.5">{parent}</p>
          </h4>
        )}
        <div className={`flex flex-col gap-4 ${getGroupContainerCls(parent)} pt-3 pb-2 mb-2`}>
          {Object.entries(columns).map(([name, specs]) => (
            <div
              key={`${parent}__${name}`}
              className="flex flex-col gap-2"
            >
              <h5 className="flex flex-row items-center gap-1.5">
                <Badge text="PARAM" type="info" textSize="text-xs"/>
                <p className="font-semibold mb-0.5">{name}</p>
              </h5>
              {!!specs.questions && (
                <Alert
                  type="info"
                  text={intl.formatMessage({
                    id: "computed_param_helper",
                    defaultMessage: "Questo parametro non viene popolato in maniera diretta dalla risposta " +
                      "data ad una singola domanda, ma viene calcolato secondo una logica interna basata " +
                      "sull'elaborazione di risposte date ad una o più domande"
                  })}
                  slim
                />
              )}
              {specs.default !== undefined && (
                <Alert
                  type="info"
                  text={intl.formatMessage(
                    {id: "param_default_value_helper", defaultMessage: "Valore di default: {value}"},
                    {value: specs.default},
                  )}
                  slim
                />
              )}
              {
                !specs.fixedNumber && Object
                  .entries(
                    specs.questions
                    ?? {"__": {label: {id: "direct_value", defaultMessage: "Valore diretto"}}}
                  )
                  .map(([subitemName, {label}], idx) => (
                    <div key={idx}>
                      <QuestionSelector
                        label={<FormattedMessage {...label} />}
                        data={object}
                        setData={updateConnection}
                        steps={data.filter(({type}) => type === "step")}
                        blocks={questions}
                        path={[parent, name, subitemName].filter((part) => part !== "__")}
                        disabled={disabled}
                      />
                    </div>
                  ))
              }
              {
                specs.fixedNumber !== undefined && (
                  <div>
                    <Input
                      type={"number"}
                      label={intl.formatMessage({
                        id: "fixed_number",
                        defaultMessage: "valore fisso"
                      })}
                      value={object?.blocks_mappings?.[parent]?.[name] ?? undefined}
                      onChange={(e) => {
                        updateConnection({
                          ...(object || {}),
                          blocks_mappings: _.set(
                            object.blocks_mappings || {},
                            [parent, name],
                            Number(e.target.value),
                          ),
                        });
                      }}
                      disabled={disabled}
                    />
                  </div>
                )}
            </div>
          ))}
        </div>
      </React.Fragment>
    ))}
  </>
}


const ServiceConfiguratorBlockCalculationConnectionGenerator = ({
  connection,
  updateConnection,
  questions,
  setSelectedOperator,
  data,
  editable = true,
}) => {

  // hooks
  const intl = useIntl();

  // utilities
  const callTypes = {
    diaphragm: intl.formatMessage({id: "diphragm", defaultMessage: "Diaphragm"}),
    vertical: intl.formatMessage({id: "vertical_connectors", defaultMessage: "Vertical connectors"}),
    wood: intl.formatMessage({id: "wood_connectors", defaultMessage: "Wood connectors"}),
  }
  const connectionObject =
    connection === "connector"
      ? {original: connection}
      : JSON.parse(connection);
  const actualQuestions = questions.filter((q) => q.type === "question")
  const filterQuestionsByInputType = (type) =>
    actualQuestions
      .filter((q) =>
        q.input_type === "connector_input"
        && q.metadata_?.type === type
      )
      .map((q) => ({name: q.name, value: q.id}));

  useEffect(() => {
    if (editable)
      updateConnection(connectionObject);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col w-full mb-8 mt-8">
      <div className=" mb-2 flex text-center justify-center">
        <p className="font-bold">CONNECTORS CALL CONFIGURATOR</p>
      </div>
      <div className="mt-2">
        <Select
          label={intl.formatMessage({id: "type", defaultMessage: "Tipologia"})}
          options={Object.keys(callTypes).map((key) => ({name: callTypes[key], value: key}))}
          value={connectionObject?.connector_type || "diphragm"}
          onChange={(e) => {
            updateConnection({...connectionObject, connector_type: e.target.value});
          }}
          disabled={!editable}
        />
      </div>
      <hr className="mt-2 mb-4 border-gray-300" />
      <div>
        {(!connectionObject?.connector_type || connectionObject.connector_type === "diaphragm") && (
          CONNETTORI_INPUT_TYPES.map((t) => (
            <Select
              key={t}
              label={`Select ${t} question`}
              options={[
                { value: "", name: "" },
                ...filterQuestionsByInputType(t),
              ]}
              value={connectionObject?.[`${t}_question`] || ""}
              onChange={(e) => {
                updateConnection({
                  ...(connectionObject || {}),
                  [`${t}_question`]: parseInt(e.target.value),
                });
              }}
              disabled={!editable}
            />
          ))
        )}
        {connectionObject?.connector_type === "vertical" && (
          <div className="flex flex-col gap-2">
            {/* -------------------- FromConfiguratorInput questions -------------------- */}
            <h3 className="text-base font-semibold">
              <FormattedMessage
                id="vertical_connectors_system_choice_title"
                defaultMessage="Scelta soluzione"
              />
            </h3>
            <QuestionSelector
              label={intl.formatMessage({
                id: "vertical_connectors_system_choice_question",
                defaultMessage: "Seleziona la domanda dalla quale l'utente selezionerà la soluzione da utilizzare una volta consultati i risultati"
              })}
              data={connectionObject}
              setData={updateConnection}
              steps={data.filter(({type}) => type === "step")}
              blocks={actualQuestions}
              path={["_system_choice_question"]}
            />
            {/* -------------------- ASDEA parameters questions -------------------- */}
            <h3 className="text-base font-semibold">
              <FormattedMessage id="parameters" defaultMessage="Parametri" />
            </h3>
            <ConnectorInputConfigurator
              structure={CONNETTORI_VERTICALI_INPUT_STRUCTURE}
              object={connectionObject}
              questions={actualQuestions}
              data={data}
              updateConnection={updateConnection}
              disabled={!editable}
            />
          </div>
        )}
        {connectionObject?.connector_type === "wood" && (
          <div className="flex flex-col gap-2">
            {/* -------------------- FromConfiguratorInput questions -------------------- */}
            <h3 className="text-base font-semibold">
              <FormattedMessage
                id="wood_connectors_system_choice_title"
                defaultMessage="Scelta connettore"
              />
            </h3>
            <QuestionSelector
              label={intl.formatMessage({
                id: "wood_connectors_system_choice_question",
                defaultMessage: "Seleziona la domanda dalla quale l'utente selezionerà il connettore da utilizzare"
              })}
              data={connectionObject}
              setData={updateConnection}
              steps={data.filter(({type}) => type === "step")}
              blocks={actualQuestions}
              path={["_connector_choice_question"]}
              disabled={!editable}
            />
            <h3 className="text-base font-semibold">
              <FormattedMessage id="parameters" defaultMessage="Parametri"/>
            </h3>
            <ConnectorInputConfigurator
              structure={CONNETTORI_LEGNO_INPUT_STRUCTURE}
              object={connectionObject}
              questions={actualQuestions}
              data={data}
              updateConnection={updateConnection}
              disabled={!editable}
            />
          </div>
        )}
      </div>
      {editable && <div className="mt-4 flex justify-end">
        <Button onClick={() => setSelectedOperator(null)}>DONE</Button>
      </div>}
    </div>
  );
};

export default ServiceConfiguratorBlockCalculationConnectionGenerator;
